import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import CallEndOutlinedIcon from '@mui/icons-material/CallEndOutlined';
import CallMissedOutgoingOutlinedIcon from '@mui/icons-material/CallMissedOutgoingOutlined';
import PhoneDisabledOutlinedIcon from '@mui/icons-material/PhoneDisabledOutlined';
import DashboardTabs from '../components/dashboard/DashboardTabs';
import NotificationPanel from '../components/dashboard/NotificationPanel';
import MissedCallsPanel from '../components/dashboard/MissedCallsPanel';
import QuickActions from '../components/dashboard/QuickActions';
import { useUser } from '../hooks/useUser';
import { listCallLogs } from '../services/calls';
import { getCallableUsers } from '../services/call';
import {
    listNotifications,
    listSeenNotifications,
    markNotificationAsSeen
} from '../services/notifications';
import { dashboardTabs } from '../components/dashboard/dashboardMockData';
import {
    getNotificationCategory,
    getNotificationDescription,
    notificationPriorityLabels
} from '../utils/notifications';
import {
    buildMissedCallRemoteUser,
    enrichNotificationsForDisplay,
    resolveAlertNotificationTarget,
    resolveAppointmentNotificationTarget
} from '../utils/notificationActions';

const dialogPaperSx = {
    borderRadius: '22px',
    boxShadow: '0px 10px 30px rgba(61, 61, 61, 0.12)'
};

const dialogTitleSx = {
    px: 3,
    pt: 3,
    pb: 1.5,
    fontWeight: 700,
    color: '#3D3D3D',
    textAlign: 'center'
};

const primaryButtonSx = {
    minHeight: 46,
    px: 2.4,
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: '16px',
    bgcolor: '#3D3D3D',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: '#2F2F2F',
        boxShadow: 'none'
    }
};

const Home = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useUser();
    const [notifications, setNotifications] = useState([]);
    const [notificationsView, setNotificationsView] = useState('unseen');
    const [notificationsPageByView, setNotificationsPageByView] = useState({ unseen: 0, seen: 0 });
    const [notificationsPageTokensByView, setNotificationsPageTokensByView] = useState({
        unseen: { 0: null },
        seen: { 0: null }
    });
    const [notificationsTotalCountByView, setNotificationsTotalCountByView] = useState({
        unseen: 0,
        seen: 0
    });
    const [hasNextNotificationsPageByView, setHasNextNotificationsPageByView] = useState({
        unseen: false,
        seen: false
    });
    const [callLogs, setCallLogs] = useState([]);
    const [callsPageIndex, setCallsPageIndex] = useState(0);
    const [callsPageTokens, setCallsPageTokens] = useState({ 0: null });
    const [callsTotalCount, setCallsTotalCount] = useState(0);
    const [hasNextCallsPage, setHasNextCallsPage] = useState(false);
    const [callableUsers, setCallableUsers] = useState([]);
    const [callResultDialog, setCallResultDialog] = useState(null);
    const [notificationToConfirm, setNotificationToConfirm] = useState(null);
    const [missedCallDialogUser, setMissedCallDialogUser] = useState(null);
    const [notificationNavigationError, setNotificationNavigationError] = useState('');
    const missedCallDialogResolvedUser = missedCallDialogUser?.userId
        ? callableUsers.find((item) => item.userId === missedCallDialogUser.userId) || null
        : null;
    const canCallMissedUser = Boolean(missedCallDialogResolvedUser?.isOnline);

    useEffect(() => {
        const result = location.state?.callResult;
        if (!result) {
            return;
        }

        setCallResultDialog(result);
        navigate(location.pathname, { replace: true, state: {} });
    }, [location.pathname, location.state, navigate]);

    const callResultTitle = useMemo(() => {
        const status = (callResultDialog?.status || '').toUpperCase();
        if (status === 'REJECTED') return 'Chamada rejeitada';
        if (status === 'IGNORED') return 'Chamada não atendida';
        return 'Chamada terminada';
    }, [callResultDialog?.status]);

    const callResultMeta = useMemo(() => {
        const status = (callResultDialog?.status || '').toUpperCase();

        if (status === 'REJECTED') {
            return {
                icon: <PhoneDisabledOutlinedIcon sx={{ fontSize: 34 }} />,
                description: 'A chamada foi rejeitada antes de ser estabelecida.'
            };
        }

        if (status === 'IGNORED') {
            return {
                icon: <CallMissedOutgoingOutlinedIcon sx={{ fontSize: 34 }} />,
                description: 'O destinatário não atendeu dentro do tempo esperado.'
            };
        }

        return {
            icon: <CallEndOutlinedIcon sx={{ fontSize: 34 }} />,
            description: 'A chamada terminou com sucesso.'
        };
    }, [callResultDialog?.status]);

    const callResultDuration = useMemo(() => {
        const total = Number(callResultDialog?.durationSec);
        if (!Number.isFinite(total) || total < 0) {
            return null;
        }
        const minutes = Math.floor(total / 60);
        const seconds = total % 60;
        const pad = (n) => String(n).padStart(2, '0');
        return `${pad(minutes)}:${pad(seconds)}`;
    }, [callResultDialog?.durationSec]);

    const handleTabClick = (tab) => {
        if (tab === 'Início') {
            navigate('/');
            return;
        }

        if (tab === 'Agendamentos') {
            navigate('/appointments');
            return;
        }

        if (tab === 'Equipa Técnica') {
            navigate('/doctors');
            return;
        }

        if (tab === 'Cuidadores e Pacientes') {
            navigate('/caregivers');
            return;
        }

        if (tab === 'Documentos') {
            navigate('/documents');
        }
    };

    useEffect(() => {
        setNotificationsView('unseen');
        setNotificationsPageByView({ unseen: 0, seen: 0 });
        setNotificationsPageTokensByView({
            unseen: { 0: null },
            seen: { 0: null }
        });
        setNotificationsTotalCountByView({
            unseen: 0,
            seen: 0
        });
        setHasNextNotificationsPageByView({
            unseen: false,
            seen: false
        });
        setCallsPageIndex(0);
        setCallsPageTokens({ 0: null });
        setCallsTotalCount(0);
        setHasNextCallsPage(false);
    }, [user.selectedRole]);

    const notificationsPageIndex = notificationsPageByView[notificationsView] || 0;
    const currentNotificationsPageToken =
        notificationsPageTokensByView[notificationsView]?.[notificationsPageIndex] || null;
    const hasNextNotificationsPage = hasNextNotificationsPageByView[notificationsView] || false;
    const currentCallsPageToken = callsPageTokens[callsPageIndex] || null;

    useEffect(() => {
        let cancelled = false;

        const loadNotifications = async () => {
            if (!user.selectedRole) {
                return;
            }

            try {
                const listNotificationsFn =
                    notificationsView === 'seen' ? listSeenNotifications : listNotifications;
                const response = await listNotificationsFn({
                    userRoleUuid: user.selectedRole,
                    paginationToken: currentNotificationsPageToken,
                    paginationLimit: 4
                });

                if (!cancelled) {
                    const rows = Array.isArray(response?.data) ? response.data : [];
                    const enrichedRows = await enrichNotificationsForDisplay({
                        notifications: rows,
                        userRoleUuid: user.selectedRole
                    });
                    setNotifications(enrichedRows);
                    const totalCount = Number(response?.headers?.['pagination-count'] || 0);
                    setNotificationsTotalCountByView((prev) => ({
                        ...prev,
                        [notificationsView]: totalCount
                    }));

                    const nextToken = response?.headers?.['pagination-token'] || null;
                    setHasNextNotificationsPageByView((prev) => ({
                        ...prev,
                        [notificationsView]: Boolean(nextToken) && enrichedRows.length >= 4
                    }));
                    if (nextToken) {
                        setNotificationsPageTokensByView((prev) => ({
                            ...prev,
                            [notificationsView]: {
                                ...(prev[notificationsView] || {}),
                                [notificationsPageIndex + 1]: nextToken
                            }
                        }));
                    }
                }
            } catch {
                if (!cancelled) {
                    setNotifications([]);
                    setNotificationsTotalCountByView((prev) => ({
                        ...prev,
                        [notificationsView]: 0
                    }));
                    setHasNextNotificationsPageByView((prev) => ({
                        ...prev,
                        [notificationsView]: false
                    }));
                }
            }
        };

        void loadNotifications();

        return () => {
            cancelled = true;
        };
    }, [currentNotificationsPageToken, notificationsPageIndex, notificationsView, user.selectedRole]);

    useEffect(() => {
        let cancelled = false;
        let intervalId = null;

        const loadCallableUsers = async () => {
            if (!user.selectedRole) {
                return;
            }

            try {
                const response = await getCallableUsers({
                    currentUserId: user.selectedRole,
                    userRoleUuid: user.selectedRole
                });

                if (!cancelled) {
                    setCallableUsers(response.users || []);
                }
            } catch {
                if (!cancelled) {
                    setCallableUsers([]);
                }
            }
        };

        void loadCallableUsers();
        intervalId = setInterval(() => {
            void loadCallableUsers();
        }, 30000);

        return () => {
            cancelled = true;
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [user.selectedRole]);

    useEffect(() => {
        const loadCallLogs = async () => {
            if (!user.selectedRole) {
                return;
            }

            try {
                const response = await listCallLogs({
                    userRoleUuid: user.selectedRole,
                    paginationToken: currentCallsPageToken,
                    paginationLimit: 4
                });
                const rows = Array.isArray(response?.data) ? response.data : [];
                setCallLogs(rows);
                setCallsTotalCount(Number(response?.headers?.['pagination-count'] || 0));

                const nextToken = response?.headers?.['pagination-token'] || null;
                setHasNextCallsPage(Boolean(nextToken) && rows.length >= 4);
                if (nextToken) {
                    setCallsPageTokens((prev) => ({
                        ...prev,
                        [callsPageIndex + 1]: nextToken
                    }));
                }
            } catch {
                setCallLogs([]);
                setCallsTotalCount(0);
                setHasNextCallsPage(false);
            }
        };

        void loadCallLogs();
    }, [callsPageIndex, currentCallsPageToken, user.selectedRole]);

    const callRows = useMemo(() => {
        const callableUserMap = new Map(
            callableUsers.map((item) => [item.userId, item])
        );

        return callLogs.map((item) => {
            const isCurrentUserSrc = item?.src?.uuid === user.selectedRole;
            const other = isCurrentUserSrc ? item?.dst : item?.src;
            const direction = isCurrentUserSrc ? 'outgoing' : 'incoming';
            const otherUser = other?.uuid ? callableUserMap.get(other.uuid) : null;

            const dateRaw = item?.created || item?.ti || item?.tf;
            const date = dateRaw ? new Date(dateRaw) : null;
            const dateText =
                date && !Number.isNaN(date.getTime())
                    ? date.toLocaleString('pt-PT')
                    : dateRaw || '--';

            return {
                id: item?.uuid || `${other?.uuid || 'u'}-${dateRaw || 'd'}`,
                userId: other?.uuid || '',
                name: other?.name || 'Utilizador',
                date: dateText,
                status: item?.state || 'UNKNOWN',
                direction,
                isOnline: Boolean(otherUser?.isOnline),
                role: other?.role || otherUser?.role || '',
                lastCallPing: otherUser?.lastCallPing || null
            };
        });
    }, [callLogs, callableUsers, user.selectedRole]);

    const notificationsTotalPages = useMemo(() => {
        const totalCount = Number(notificationsTotalCountByView[notificationsView] || 0);
        if (totalCount <= 0) {
            return 1;
        }

        return Math.max(1, Math.ceil(totalCount / 4));
    }, [notificationsTotalCountByView, notificationsView]);

    const callsTotalPages = useMemo(() => {
        if (callsTotalCount <= 0) {
            return 1;
        }

        return Math.max(1, Math.ceil(callsTotalCount / 4));
    }, [callsTotalCount]);

    const notificationRows = useMemo(
        () =>
            notifications.map((notification, index) => ({
                id: notification?.uuid || `notification-${index}`,
                uuid: notification?.uuid || '',
                type: (notification?.type || '').toUpperCase(),
                category: getNotificationCategory(notification),
                description: getNotificationDescription(notification),
                status:
                    notificationPriorityLabels[(notification?.priority || '').toUpperCase()] ||
                    '',
                highlight: (notification?.priority || '').toUpperCase() === 'HIGH',
                raw: notification
            })),
        [notifications]
    );

    const handleAskMarkNotificationSeen = (row) => {
        if (!row?.uuid) {
            return;
        }

        setNotificationToConfirm(row);
    };

    const handleNotificationRowClick = async (row) => {
        const notification = row?.raw;
        const type = (notification?.type || '').toUpperCase();

        if (!notification || !user.selectedRole) {
            return;
        }

        if (type === 'MISSED_CALL') {
            const remoteUser = buildMissedCallRemoteUser(notification);
            if (remoteUser) {
                setMissedCallDialogUser(remoteUser);
            }
            return;
        }

        if (type === 'APPT_REMINDER') {
            try {
                const target = await resolveAppointmentNotificationTarget({
                    notification,
                    userRoleUuid: user.selectedRole
                });

                if (target?.navigateTo) {
                    navigate(target.navigateTo, {
                        state: target.navigationState
                    });
                }
            } catch {
                // keep silent for now
            }
            return;
        }

        if (type === 'ALERT') {
            try {
                const target = await resolveAlertNotificationTarget({
                    notification,
                    userRoleUuid: user.selectedRole
                });

                if (target?.caregiverRoleUuid) {
                    navigate(
                        `/caregivers/${target.caregiverRoleUuid}?tab=monitorizacao&metric=${target.measurementType}`
                    );
                    return;
                }

                if (target?.unresolvedPatientCaregiver) {
                    setNotificationNavigationError(
                        'Este alerta está associado a um paciente e ainda não foi possível abrir automaticamente o cuidador relacionado neste ambiente.'
                    );
                }
            } catch {
                setNotificationNavigationError(
                    'Não foi possível abrir automaticamente esta notificação.'
                );
            }
        }
    };

    const handleStartMissedCall = () => {
        if (!missedCallDialogUser?.userId) {
            setMissedCallDialogUser(null);
            return;
        }

        navigate(`/call/${missedCallDialogUser.userId}`, {
            state: {
                autoDial: true,
                user: missedCallDialogUser
            }
        });
        setMissedCallDialogUser(null);
    };

    const handleConfirmMarkNotificationSeen = async () => {
        if (!notificationToConfirm?.uuid || !user.selectedRole) {
            setNotificationToConfirm(null);
            return;
        }

        try {
            await markNotificationAsSeen({
                userRoleUuid: user.selectedRole,
                notificationUuid: notificationToConfirm.uuid
            });

            setNotifications((prev) =>
                prev.filter((item) => item.uuid !== notificationToConfirm.uuid)
            );
            setNotificationsPageByView((prev) => ({
                ...prev,
                unseen: 0
            }));
            setNotificationsPageTokensByView((prev) => ({
                ...prev,
                unseen: { 0: null }
            }));
            setHasNextNotificationsPageByView((prev) => ({
                ...prev,
                unseen: false
            }));
            setNotificationToConfirm(null);
        } catch {
            setNotificationToConfirm(null);
        }
    };

    return (
        <Box
            sx={{
                px: { xs: 1.5, md: 2 },
                pb: { xs: 1.5, md: 2 },
                pt: 0,
                bgcolor: '#FFFFFF'
            }}
        >
            <Box sx={{ mt: 0 }}>
                <DashboardTabs tabs={dashboardTabs} onTabClick={handleTabClick} activeTab="Início" />
            </Box>

            <Paper variant="elevation=0" sx={{ p: 2, pb: 2, bgcolor: '#ffffff' }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                        gap: 2
                    }}
                >
                    <NotificationPanel
                        title="Últimas Notificações"
                        rows={notificationRows}
                        view={notificationsView}
                        onChangeView={(nextView) => {
                            if (nextView === notificationsView) {
                                return;
                            }
                            setNotificationsView(nextView);
                        }}
                        onMarkSeen={handleAskMarkNotificationSeen}
                        onRowClick={handleNotificationRowClick}
                        onPrevPage={() =>
                            setNotificationsPageByView((prev) => ({
                                ...prev,
                                [notificationsView]: Math.max((prev[notificationsView] || 0) - 1, 0)
                            }))
                        }
                        onNextPage={() => {
                            if (!hasNextNotificationsPage) return;
                            setNotificationsPageByView((prev) => ({
                                ...prev,
                                [notificationsView]: (prev[notificationsView] || 0) + 1
                            }));
                        }}
                        disablePrev={notificationsPageIndex <= 0}
                        disableNext={!hasNextNotificationsPage}
                        pageLabel={`${notificationsPageIndex + 1} de ${notificationsTotalPages}`}
                    />
                    <MissedCallsPanel
                        title="Últimas Chamadas"
                        rows={callRows}
                        onVideoCall={(row) => {
                            if (!row?.userId) {
                                return;
                            }

                            navigate(`/call/${row.userId}`, {
                                state: {
                                    autoDial: true,
                                    user: row,
                                    wakeIfOffline: true
                                }
                            });
                        }}
                        onPrevPage={() => setCallsPageIndex((prev) => Math.max(prev - 1, 0))}
                        onNextPage={() => {
                            if (hasNextCallsPage) {
                                setCallsPageIndex((prev) => prev + 1);
                            }
                        }}
                        disablePrev={callsPageIndex <= 0}
                        disableNext={!hasNextCallsPage}
                        pageLabel={`${callsPageIndex + 1} de ${callsTotalPages}`}
                    />
                </Box>

                <QuickActions />
            </Paper>

            <Dialog
                open={Boolean(callResultDialog)}
                onClose={() => setCallResultDialog(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>{callResultTitle}</DialogTitle>
                <DialogContent sx={{ px: 3, pb: 1.5, pt: 0.5 }}>
                    <Stack spacing={2.25} alignItems="center" textAlign="center">
                        <Box
                            sx={{
                                width: 78,
                                height: 78,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(214, 55, 49, 0.08)',
                                color: '#D63731',
                                border: '1px solid rgba(214, 55, 49, 0.18)'
                            }}
                        >
                            {callResultMeta.icon}
                        </Box>
                        <Stack spacing={0.75} alignItems="center">
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
                                {callResultMeta.description}
                            </Typography>
                        </Stack>
                        <Paper
                            sx={{
                                width: '40%',
                                p: 1.5,
                                borderRadius: '16px',
                                bgcolor: '#F7F7F7',
                                boxShadow: 'none'
                            }}
                        >
                            <Typography variant="body2" color="#3D3D3D" fontWeight={600}>
                                {callResultDuration
                                    ? `Duração total: ${callResultDuration}`
                                    : 'Sem duração registada.'}
                            </Typography>
                        </Paper>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'center' }}>
                    <Button onClick={() => setCallResultDialog(null)} autoFocus variant="contained" sx={primaryButtonSx}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(notificationToConfirm)}
                onClose={() => setNotificationToConfirm(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Marcar notificação como vista</DialogTitle>
                <DialogContent sx={{ px: 3, pb: 1, pt: 0.5 }}>
                    <Typography variant="body2" color="#3D3D3D">
                        Tem a certeza que quer marcar esta notificação como vista?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
                    <Button
                        onClick={() => setNotificationToConfirm(null)}
                        variant="outlined"
                        sx={{
                            minHeight: 46,
                            px: 2.4,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '16px',
                            color: '#3D3D3D',
                            borderColor: '#D9D9D9',
                            borderWidth: '2px',
                            '&:hover': {
                                bgcolor: '#F7F7F7',
                                borderColor: '#CFCFCF',
                                borderWidth: '2px'
                            }
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={handleConfirmMarkNotificationSeen} sx={primaryButtonSx}>
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(missedCallDialogUser)}
                onClose={() => setMissedCallDialogUser(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Chamada perdida</DialogTitle>
                <DialogContent sx={{ px: 3, pb: 1, pt: 0.5 }}>
                    <Typography variant="body2" color="#3D3D3D">
                        Quer ligar para <strong>{missedCallDialogUser?.displayName || 'este utilizador'}</strong>?
                    </Typography>
                    {!canCallMissedUser ? (
                        <Typography variant="body2" color="#6F6F6F" sx={{ mt: 1 }}>
                            Este utilizador está offline de momento.
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
                    <Button
                        onClick={() => setMissedCallDialogUser(null)}
                        variant="outlined"
                        sx={{
                            minHeight: 46,
                            px: 2.4,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '16px',
                            color: '#3D3D3D',
                            borderColor: '#D9D9D9',
                            borderWidth: '2px',
                            '&:hover': {
                                bgcolor: '#F7F7F7',
                                borderColor: '#CFCFCF',
                                borderWidth: '2px'
                            }
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleStartMissedCall}
                        sx={primaryButtonSx}
                        disabled={!canCallMissedUser}
                    >
                        Ligar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(notificationNavigationError)}
                onClose={() => setNotificationNavigationError('')}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Ação indisponível</DialogTitle>
                <DialogContent sx={{ px: 3, pb: 1, pt: 0.5 }}>
                    <Typography variant="body2" color="#3D3D3D">
                        {notificationNavigationError}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
                    <Button variant="contained" onClick={() => setNotificationNavigationError('')} sx={primaryButtonSx}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Home;






